/**
 * Shared edge function client for Demo Mode flows.
 * Retries transient gateway errors (502, 503, 504) and network failures
 * with exponential backoff + jitter.
 */

import { supabase } from '@/integrations/supabase/client';

const RETRYABLE_STATUSES = new Set([502, 503, 504]);

export class EdgeFunctionError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'EdgeFunctionError';
  }
}

export interface CallEdgeFunctionOptions {
  maxRetries?: number;
  baseDelayMs?: number;
}

export async function getAccessToken(): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return session.access_token;
}

export async function callEdgeFunction<T>(
  functionName: string,
  method: 'GET' | 'POST' | 'PATCH' = 'GET',
  body?: unknown,
  params?: URLSearchParams,
  options?: CallEdgeFunctionOptions,
): Promise<T> {
  const { maxRetries = 3, baseDelayMs = 1000 } = options ?? {};
  const token = await getAccessToken();

  let url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`;
  if (params) {
    url += `?${params.toString()}`;
  }

  const init: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  };

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    let response: Response;
    try {
      response = await fetch(url, init);
    } catch (fetchError) {
      console.error(`[edgeFunctionClient] ${functionName} fetch error:`, {
        error: fetchError instanceof Error ? fetchError.message : String(fetchError),
        url,
        origin: typeof window !== 'undefined' ? window.location.origin : 'unknown',
        attempt: attempt + 1,
      });
      if (attempt < maxRetries) {
        const jitter = 0.5 + Math.random() * 0.5;
        const delay = baseDelayMs * Math.pow(2, attempt) * jitter;
        console.warn(
          `[edgeFunctionClient] ${functionName} network error, retry ${attempt + 1}/${maxRetries} in ${Math.round(delay)}ms`,
        );
        await new Promise((r) => setTimeout(r, delay));
        continue;
      }
      throw fetchError;
    }

    if (response.ok) {
      return (await response.json()) as T;
    }

    const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    const errorMsg = data?.error;
    const details = data?.details;
    const message =
      typeof errorMsg === 'string' && errorMsg
        ? details && typeof details === 'string' && details !== errorMsg
          ? `${errorMsg}: ${details}`
          : errorMsg
        : `Request failed with status ${response.status}`;

    if (!RETRYABLE_STATUSES.has(response.status) || attempt === maxRetries) {
      const err = new EdgeFunctionError(
        String(message),
        response.status,
        (data as Record<string, unknown>)?.code as string | undefined,
      );
      throw err;
    }

    const jitter = 0.5 + Math.random() * 0.5;
    const delay = baseDelayMs * Math.pow(2, attempt) * jitter;
    console.warn(
      `[edgeFunctionClient] ${functionName} returned ${response.status}, retry ${attempt + 1}/${maxRetries} in ${Math.round(delay)}ms`,
    );
    await new Promise((r) => setTimeout(r, delay));
  }

  throw new Error('Unreachable');
}
